/**
 * Ordini di uno SKU negozio per negozio (SPJ-1364): dati e calcoli del selettore
 * multi-negozio del popup ordini. Niente React qui, cosi' la logica si testa da sola.
 */
import type { OrderTooltipData } from './SkuOrderForecastBadges';

/** Una riga di GET /optimization/sku-orders-by-shop (via azione optim-sku-orders-by-shop). */
export type SkuOrdersShopEntry = {
  cod_negozio: string;
  des_negozio?: string | null;
  quantity: number;
  quantity_venduta: number;
  taglie: Record<string, number>;
  taglie_venduta: Record<string, number>;
};

/** Carica gli ordini per negozio di uno SKU. */
export type LoadSkuOrdersByShop = (sku: string) => Promise<SkuOrdersShopEntry[]>;

/** Separatore dei negozi raggruppati in un'unica entry (stesso di parse_cod_negozio lato API). */
const SHOP_GROUP_SEPARATOR = '|,|';

/** Negozi reali di un id di negozio del FE: un id singolo, un gruppo, oppure la vista area (nessuno). */
export function parseShopIds(shopId?: string | null): string[] {
  if (!shopId || shopId === 'area') return [];
  return shopId
    .split(SHOP_GROUP_SEPARATOR)
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

/** Stagione dello SKU: le prime tre cifre (lo SKU e' `${cod_stagione}${modello}${colore}`). */
export function seasonFromSku(sku: string): string | undefined {
  return /^(\d{3})/.exec(sku.trim())?.[1];
}

/**
 * Parametri dell'azione optim-sku-orders-by-shop. La stagione viene dallo SKU; la linea dal
 * dataset della run, poi dallo stato (persona fa lo stesso fallback). La consegna non serve:
 * l'API cerca lo SKU come sku e, se assente, come sku_precollezione.
 */
export function buildSkuOrdersByShopParams(sku: string, appState?: Record<string, any> | null): Record<string, unknown> {
  const dataset = appState?.['optim-dataset'] && typeof appState['optim-dataset'] === 'object' ? appState['optim-dataset'] : {};
  const params: Record<string, unknown> = { 'optim-sku': sku };
  const codStagione = seasonFromSku(sku) ?? dataset.cod_stagione ?? appState?.['optim-season'];
  const gender = dataset.gender ?? appState?.['optim-gender'];
  if (codStagione !== undefined && codStagione !== null && String(codStagione).trim() !== '') params['optim-cod-stagione'] = codStagione;
  if (gender !== undefined && gender !== null && String(gender).trim() !== '') params['optim-gender'] = gender;
  return params;
}

/** Etichetta di un negozio nel selettore. */
export function shopLabel(entry: Pick<SkuOrdersShopEntry, 'cod_negozio' | 'des_negozio'>): string {
  return entry.des_negozio ? `${entry.cod_negozio} - ${entry.des_negozio}` : entry.cod_negozio;
}

/** Negozi che corrispondono alla ricerca, su codice o descrizione, senza distinguere maiuscole. */
export function filterShops<T extends Pick<SkuOrdersShopEntry, 'cod_negozio' | 'des_negozio'>>(entries: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries;
  return entries.filter(
    (entry) => entry.cod_negozio.toLowerCase().includes(q) || (entry.des_negozio ?? '').toLowerCase().includes(q),
  );
}

export function sameSelection(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
}

/**
 * Somma dei negozi selezionati nella forma del popup ordini. La run collegata non si somma:
 * esiste solo per i negozi della run, non per gli altri.
 */
export function sumShopOrders(entries: SkuOrdersShopEntry[], selected: ReadonlySet<string>): OrderTooltipData {
  let orderQuantity = 0;
  let orderQuantityVenduta = 0;
  const taglie: Record<string, number> = {};
  const taglieVenduta: Record<string, number> = {};
  entries.forEach((entry) => {
    if (!selected.has(entry.cod_negozio)) return;
    orderQuantity += Number(entry.quantity || 0);
    orderQuantityVenduta += Number(entry.quantity_venduta || 0);
    Object.entries(entry.taglie || {}).forEach(([size, n]) => {
      taglie[size] = (taglie[size] || 0) + Number(n || 0);
    });
    Object.entries(entry.taglie_venduta || {}).forEach(([size, n]) => {
      taglieVenduta[size] = (taglieVenduta[size] || 0) + Number(n || 0);
    });
  });
  return {
    orderQuantity,
    taglieOrder: Object.keys(taglie).length ? { selezione: taglie } : undefined,
    orderQuantityVenduta,
    taglieOrderVenduta: Object.keys(taglieVenduta).length ? { selezione: taglieVenduta } : undefined,
  };
}
